import { useCallback, useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

/** Ask the browser to look for a new worker at most this often. */
const UPDATE_INTERVAL_MS = 30 * 60 * 1000;

export function serviceWorkerUrl(root: Document = document) {
  const entry = root.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/"]',
  );
  const buildId = entry?.src.split("/").pop();
  return buildId ? `/sw.js?build=${encodeURIComponent(buildId)}` : "/sw.js";
}

/**
 * Registers the offline app-shell service worker and surfaces new deployments.
 *
 * In development the effect actively unregisters any leftover worker and drops
 * its caches — a stale prod shell on localhost otherwise masks code changes and
 * breaks HMR.
 *
 * The worker installs but parks itself rather than calling `skipWaiting()`, so
 * a long-lived installed window is never swapped out mid-form. When a new
 * version is waiting we offer a reload; accepting it hands over and refreshes.
 */
export function ServiceWorkerRegistrar() {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    if (process.env.NODE_ENV !== "production") {
      // A worker left behind by a production build on the same origin keeps
      // serving its cached chunks into `next dev`, which silently hides every
      // code change. Tear it down rather than just declining to register.
      void navigator.serviceWorker.getRegistrations().then((regs) => {
        for (const reg of regs) void reg.unregister();
      });
      if ("caches" in window) {
        void caches.keys().then((keys) => {
          for (const key of keys) {
            if (key.startsWith("steptember-")) void caches.delete(key);
          }
        });
      }
      return;
    }

    let registration: ServiceWorkerRegistration | undefined;
    let timer: ReturnType<typeof setInterval> | undefined;
    let reloading = false;

    // The new worker took control — bring the page up to date with it.
    const onControllerChange = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    const track = (worker: ServiceWorker | null | undefined) => {
      if (!worker) return;
      // A worker only supersedes something if a controller is already in place;
      // on a first-ever install there is nothing to tell the user about.
      if (worker.state === "installed" && navigator.serviceWorker.controller) {
        setWaiting(worker);
        return;
      }
      worker.addEventListener("statechange", () => {
        if (worker.state === "installed" && navigator.serviceWorker.controller) {
          setWaiting(worker);
        }
      });
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void registration?.update().catch(() => {});
      }
    };

    const register = async () => {
      try {
        registration = await navigator.serviceWorker.register(serviceWorkerUrl());
      } catch {
        // Registration failure must never surface to the user — the app works
        // perfectly well without an offline shell.
        return;
      }

      const reg = registration;
      track(reg.waiting ?? reg.installing);
      reg.addEventListener("updatefound", () => track(reg.installing));

      timer = setInterval(() => void reg.update().catch(() => {}), UPDATE_INTERVAL_MS);
      // Returning to a backgrounded app is the moment an update matters most.
      document.addEventListener("visibilitychange", onVisible);
    };

    if (document.readyState === "complete") {
      void register();
    } else {
      window.addEventListener("load", () => void register());
    }

    return () => {
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) clearInterval(timer);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    waiting?.postMessage({ type: "SKIP_WAITING" });
  }, [waiting]);

  if (!waiting || dismissed) return null;

  return (
    <div className="no-print fixed inset-x-3 bottom-24 z-[120] mx-auto max-w-md rounded-2xl border border-border bg-surface p-4 shadow-xl sm:left-auto sm:right-4 lg:bottom-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--brand-primary)] text-white">
          <RefreshCw className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-text">A new version is ready</p>
          <p className="mt-0.5 text-sm text-text-muted">
            Reload to pick up the latest changes. Nothing you have logged is lost.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={applyUpdate}
              className="inline-flex h-9 items-center rounded-xl bg-[var(--brand-primary)] px-4 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="inline-flex h-9 items-center rounded-xl px-4 text-sm font-semibold text-text-muted transition hover:bg-surface-muted"
            >
              Later
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss update notice"
          className="text-text-subtle transition hover:text-text"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
